import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Collapse, Form, Input, InputNumber, Select } from "antd";
import type {
  TCommunicationChangeAction,
  TCommunicationAuditEntry,
  TCommunicationQuestion,
  TCommunicationReferenceOption,
  TCommunicationSuggestionStatus,
  TCommunicationTask,
  TCommunicationTaskFormValues,
} from "../../helpers/communication-types-helper";
import {
  buildCommunicationLogisticsPreview,
} from "../../helpers/communication-preview-helper";
import { formatCommunicationDateTime } from "../../helpers/communication-date-helper";
import { localizeCommunicationLookupValue } from "../../helpers/communication-localize-helper";
import {
  buildCommunicationTaskSteps,
  TCommunicationTaskStep,
  TCommunicationTaskStepIconKey,
} from "../../helpers/communication-step-helper";
import {
  getCommunicationConfidenceTone,
  normalizeCommunicationConfidence,
} from "../../helpers/communication-confidence-helper";
import CommunicationOriginalEmailField from "../../fields/communication-original-email-field";
import CommunicationConst from "../../communication-const";
import { Localize, localizeByKeyStatic } from "components/service";
import Icons from "components/icons";

const { TextArea } = Input;

interface ICommunicationTaskFormProps {
  task: TCommunicationTask;
  canEditRaw: boolean;
  canApprove: boolean;
  isAuditView?: boolean;
  equipmentOptions: TCommunicationReferenceOption[];
  flooringOptions: TCommunicationReferenceOption[];
  organizerOptions: TCommunicationReferenceOption[];
  exhibitionOptions: TCommunicationReferenceOption[];
  auditEntry?: TCommunicationAuditEntry;
  onSave: (taskId: string, values: TCommunicationTaskFormValues) => void;
  onApprove: (taskId: string, values: TCommunicationTaskFormValues) => void;
  action?: () => void;
  showModal?: boolean;
}

const actionOptions = [
  { value: "add", label: localizeByKeyStatic("COMMUNICATION.Action_Add") },
  { value: "remove", label: localizeByKeyStatic("COMMUNICATION.Action_Remove") },
  { value: "change", label: localizeByKeyStatic("COMMUNICATION.Action_Change") },
];

const actionLabelKeyMap: Record<TCommunicationChangeAction, string> = {
  add: "COMMUNICATION.Action_Add",
  remove: "COMMUNICATION.Action_Remove",
  change: "COMMUNICATION.Action_Change",
};

const questionStatusOptions = [
  { value: "open", label: localizeByKeyStatic("COMMUNICATION.QuestionStatus_Open") },
  { value: "reviewed", label: localizeByKeyStatic("COMMUNICATION.QuestionStatus_Reviewed") },
];

const questionStatusLabelMap: Record<TCommunicationQuestion["status"], string> = {
  open: localizeByKeyStatic("COMMUNICATION.QuestionStatus_Open"),
  reviewed: localizeByKeyStatic("COMMUNICATION.QuestionStatus_Reviewed"),
};

const suggestionStatusLabelKeyMap: Record<TCommunicationSuggestionStatus, string> = {
  pending: "COMMUNICATION.EquipmentSuggestion_Pending",
  confirmed: "COMMUNICATION.EquipmentSuggestion_Confirmed",
  changed: "COMMUNICATION.EquipmentSuggestion_Changed",
};

const createEmptyEquipmentChange = (taskId: string) => ({
  id: `new-${taskId}-${Date.now()}`,
  action: "add" as const,
  item_label: "",
  qty: 1,
  color: null,
  equipment_group_id: undefined,
  suggested_equipment_group_id: undefined,
  suggestion_status: undefined,
});

const createEmptyQuestion = (taskId: string): TCommunicationQuestion => ({
  id: `question-${taskId}-${Date.now()}`,
  text: "",
  status: "open",
});

const stripStepTitleIndex = (label: string): string =>
  label.replace(/^\s*\d+\.\s*/u, "").trim();

const stepIconMap: Record<TCommunicationTaskStepIconKey, React.ElementType> = {
  check: Icons.Check,
  attention: Icons.Attention,
  note: Icons.Note,
  lock: Icons.Padlock,
};

const matchingFieldNames = [ "event_name", "company_name", "stand_number" ] as const;

type TValidationFieldName = string | (string | number)[];
type TValidateFieldsError = {
  errorFields?: Array<{
    name: (string | number)[];
  }>;
};

const normalizeCollapseKeys = (keys?: string | string[] | null): string[] =>
  Array.isArray(keys) ? keys : keys ? [ keys ] : [];

const hasStandUpdates = (standUpdates?: Partial<TCommunicationTaskFormValues["stand_updates"]> | null): boolean =>
  Boolean(
    (typeof standUpdates?.flooring_id === "string"
      ? standUpdates.flooring_id.trim()
      : standUpdates?.flooring_id)
    || (typeof standUpdates?.flooring_color === "string"
      ? standUpdates.flooring_color.trim()
      : standUpdates?.flooring_color)
  );

const hasManagerNote = (managerNote?: string | null): boolean =>
  Boolean(managerNote?.trim());

const resolveEquipmentGroupId = (
  equipmentGroupId: string | null | undefined,
  equipmentId: string | null | undefined,
  equipmentIdToGroupIdMap: Map<string, string>
): string | undefined => {
  if (equipmentGroupId?.trim()) {
    return equipmentGroupId;
  }

  if (equipmentId?.trim()) {
    return equipmentIdToGroupIdMap.get(equipmentId);
  }

  return undefined;
};

const buildMergedEquipmentOptions = (
  equipmentOptions: TCommunicationReferenceOption[],
  equipmentChanges: TCommunicationTask["final_changes"]["equipment_changes"],
  equipmentIdToGroupIdMap: Map<string, string>
): TCommunicationReferenceOption[] => {
  const optionMap = new Map(
    equipmentOptions.map((option) => [ option.value, option ])
  );

  equipmentChanges.forEach((change) => {
    const resolvedGroupId = resolveEquipmentGroupId(
      change.equipment_group_id || change.suggested_equipment_group_id,
      change.equipment_id,
      equipmentIdToGroupIdMap
    );

    if (!resolvedGroupId || optionMap.has(resolvedGroupId)) {
      return;
    }

    optionMap.set(resolvedGroupId, {
      value: resolvedGroupId,
      label: change.equipment_title || change.item_label || resolvedGroupId,
      rawId: change.equipment_id,
    });
  });

  return Array.from(optionMap.values());
};

type TEquipmentSuggestionMeta = {
  suggestedGroupId: string;
  status: TCommunicationSuggestionStatus;
};

const getEquipmentSuggestionMeta = (
  change: TCommunicationTaskFormValues["equipment_changes"][number]
): TEquipmentSuggestionMeta | null => {
  if (!change?.suggested_equipment_group_id) {
    return null;
  }

  return {
    suggestedGroupId: change.suggested_equipment_group_id,
    status: change.suggestion_status || "pending",
  };
};

const getEffectiveEquipmentGroupId = (
  change?: TCommunicationTaskFormValues["equipment_changes"][number] | null
): string | undefined =>
  change?.equipment_group_id
  || (change?.suggestion_status !== "changed" ? change?.suggested_equipment_group_id || undefined : undefined);

const isBlankEquipmentChange = (
  change?: TCommunicationTaskFormValues["equipment_changes"][number] | null
): boolean =>
  (change?.action || "add") === "add"
  && (typeof change?.qty === "number" ? change.qty : 1) === 1
  && !getEffectiveEquipmentGroupId(change)
  && !change?.equipment_id?.trim()
  && !change?.item_label?.trim()
  && !change?.equipment_title?.trim()
  && !change?.color?.trim();

const shouldIgnoreEquipmentChangeValidation = (
  change: TCommunicationTaskFormValues["equipment_changes"][number] | undefined,
  index: number,
  changes: TCommunicationTaskFormValues["equipment_changes"],
  hasExistingEquipmentChanges: boolean
): boolean =>
  !hasExistingEquipmentChanges
  && changes.length === 1
  && index === 0
  && isBlankEquipmentChange(change);

const getEquipmentValidationMessage = (
  change: TCommunicationTaskFormValues["equipment_changes"][number] | undefined
): string => {
  const sourceLabel =
    change?.equipment_title?.trim()
    || change?.item_label?.trim()
    || "";

  if (sourceLabel) {
    return localizeByKeyStatic("COMMUNICATION.Validation_SelectEquipmentForItemOrDelete", {
      item: sourceLabel,
    });
  }

  return localizeByKeyStatic("COMMUNICATION.Validation_SelectEquipmentOrDelete");
};

const mapTaskToFormValues = (
  task: TCommunicationTask,
  equipmentIdToGroupIdMap: Map<string, string> = new Map()
): TCommunicationTaskFormValues => ({
  event_name: task.event_name || "",
  company_name: task.company_name || "",
  stand_number: task.stand_number || "",
  organizer_id: task.organizer_id || undefined,
  exhibition_type_id: task.exhibition_type_id || undefined,
  manager_note: task.final_changes.manager_note || "",
  equipment_changes:
    task.final_changes.equipment_changes.length > 0
      ? task.final_changes.equipment_changes.map((change) => {
        const suggestedEquipmentGroupId = resolveEquipmentGroupId(
          change.suggested_equipment_group_id || change.equipment_group_id,
          change.equipment_id,
          equipmentIdToGroupIdMap
        );
        const resolvedEquipmentGroupId = resolveEquipmentGroupId(
          change.equipment_group_id || suggestedEquipmentGroupId,
          change.equipment_id,
          equipmentIdToGroupIdMap
        );

        return {
          ...change,
          color: localizeCommunicationLookupValue(change.color) || null,
          equipment_group_id: resolvedEquipmentGroupId,
          suggested_equipment_group_id: suggestedEquipmentGroupId,
          suggestion_status:
            suggestedEquipmentGroupId
              ? change.suggestion_status || "pending"
              : change.suggestion_status || undefined,
        };
      })
      : [createEmptyEquipmentChange(task.id)],
  stand_updates: {
    flooring_id: task.final_changes.stand_updates.flooring_id || undefined,
    flooring_color:
      localizeCommunicationLookupValue(task.final_changes.stand_updates.flooring_color) || "",
  },
  questions: task.questions.length > 0 ? task.questions : [],
});

const getInitialStep = (task: TCommunicationTask): number => {
  if (task.status === CommunicationConst.statuses.APPROVED) {
    return 3;
  }

  return task.event_name?.trim() && task.company_name?.trim() && task.stand_number?.trim() ? 2 : 1;
};

const getInitialUnlockedStep = (
  task: TCommunicationTask,
  isAuditView?: boolean
): TCommunicationTaskStep["id"] => {
  if (isAuditView || task.status === CommunicationConst.statuses.APPROVED) {
    return 3;
  }

  return getInitialStep(task) as TCommunicationTaskStep["id"];
};

const buildFormValueSnapshot = (
  task: TCommunicationTask,
  rawValues: Partial<TCommunicationTaskFormValues>,
  resolvedValues: {
    event_name: string;
    company_name: string;
    stand_number: string;
    equipment_changes: TCommunicationTaskFormValues["equipment_changes"];
    stand_updates: TCommunicationTaskFormValues["stand_updates"];
    questions: TCommunicationTaskFormValues["questions"];
  }
): TCommunicationTaskFormValues => ({
  event_name: resolvedValues.event_name,
  company_name: resolvedValues.company_name,
  stand_number: resolvedValues.stand_number,
  organizer_id: rawValues.organizer_id || task.organizer_id || undefined,
  exhibition_type_id: rawValues.exhibition_type_id || task.exhibition_type_id || undefined,
  manager_note: rawValues.manager_note || task.final_changes.manager_note || "",
  equipment_changes: resolvedValues.equipment_changes,
  stand_updates: resolvedValues.stand_updates,
  questions: resolvedValues.questions,
});

const CommunicationTaskForm: React.FC<ICommunicationTaskFormProps> = ({
  task,
  canApprove,
  equipmentOptions,
  flooringOptions,
  organizerOptions,
  exhibitionOptions,
  auditEntry,
  isAuditView,
  onApprove,
  action,
  showModal,
}) => {
  const [form] = Form.useForm<TCommunicationTaskFormValues>();
  const [currentStep, setCurrentStep] = useState<number>(getInitialStep(task));
  const [maxUnlockedStep, setMaxUnlockedStep] = useState<TCommunicationTaskStep["id"]>(
    getInitialUnlockedStep(task, isAuditView)
  );
  const [openEquipmentField, setOpenEquipmentField] = useState<number | null>(null);
  const [openContextPanels, setOpenContextPanels] = useState<string[]>([]);
  const [showStandUpdates, setShowStandUpdates] = useState<boolean>(
    hasStandUpdates(task.extracted_changes.stand_updates) || hasStandUpdates(task.final_changes.stand_updates)
  );
  const [showManagerNote, setShowManagerNote] = useState<boolean>(
    hasManagerNote(task.final_changes.manager_note)
  );
  const confidenceValue = normalizeCommunicationConfidence(task.confidence);
  const confidenceTone = getCommunicationConfidenceTone(confidenceValue);
  const equipmentIdToGroupIdMap = useMemo(
    () =>
      new Map(
        equipmentOptions
          .filter((option) => option.rawId)
          .map((option) => [ option.rawId as string, option.value ])
      ),
    [equipmentOptions]
  );

  const watchedQuestions = Form.useWatch("questions", form);
  const watchedEventName = Form.useWatch("event_name", form);
  const watchedCompanyName = Form.useWatch("company_name", form);
  const watchedStandNumber = Form.useWatch("stand_number", form);
  const watchedEquipmentChanges = Form.useWatch("equipment_changes", form);
  const watchedStandUpdates = Form.useWatch("stand_updates", form);
  const resolvedQuestions =
    watchedQuestions ?? form.getFieldValue("questions") ?? task.questions;
  const resolvedEventName =
    watchedEventName ?? form.getFieldValue("event_name") ?? task.event_name ?? "";
  const resolvedCompanyName =
    watchedCompanyName ?? form.getFieldValue("company_name") ?? task.company_name ?? "";
  const resolvedStandNumber =
    watchedStandNumber ?? form.getFieldValue("stand_number") ?? task.stand_number ?? "";
  const resolvedEquipmentChanges =
    watchedEquipmentChanges
    ?? form.getFieldValue("equipment_changes")
    ?? task.final_changes.equipment_changes;
  const resolvedStandUpdates =
    watchedStandUpdates
    ?? form.getFieldValue("stand_updates")
    ?? task.final_changes.stand_updates
    ?? {};
  const isFinalizedTask = Boolean(isAuditView || task.status === CommunicationConst.statuses.APPROVED);
  const hasExistingEquipmentChanges = Boolean(
    task.extracted_changes.equipment_changes.length || task.final_changes.equipment_changes.length
  );
  const openQuestionsCount = resolvedQuestions.filter((question) => question?.status === "open").length;
  const pendingEquipmentSuggestionsCount = resolvedEquipmentChanges.filter((change) => {
    const suggestionMeta = getEquipmentSuggestionMeta(change);
    return suggestionMeta?.status === "pending";
  }).length;
  const unresolvedEquipmentSelectionCount = resolvedEquipmentChanges.filter((change, index, changes) => {
    if (shouldIgnoreEquipmentChangeValidation(change, index, changes, hasExistingEquipmentChanges)) {
      return false;
    }

    return !getEffectiveEquipmentGroupId(change);
  }).length;
  const hasStepTwoBlockingIssues = resolvedEquipmentChanges.some((change, index, changes) => {
    if (shouldIgnoreEquipmentChangeValidation(change, index, changes, hasExistingEquipmentChanges)) {
      return false;
    }

    return !getEffectiveEquipmentGroupId(change) || !change?.qty || change.qty < 1;
  });
  const hasReviewableEquipmentChanges = resolvedEquipmentChanges.some((change, index, changes) => {
    if (shouldIgnoreEquipmentChangeValidation(change, index, changes, hasExistingEquipmentChanges)) {
      return false;
    }

    return !isBlankEquipmentChange(change);
  });
  const reviewChangesCount = pendingEquipmentSuggestionsCount + unresolvedEquipmentSelectionCount;
  const isMatchComplete = Boolean(
    resolvedEventName?.trim() && resolvedCompanyName?.trim() && resolvedStandNumber?.trim()
  );
  const matchingMissingFieldLabels = useMemo(() => {
    const missingFields: string[] = [];

    if (!resolvedEventName?.trim()) {
      missingFields.push(localizeByKeyStatic("FORMS.Input_Label_EventName"));
    }

    if (!resolvedCompanyName?.trim()) {
      missingFields.push(localizeByKeyStatic("FORMS.Input_Label_CompanyName"));
    }

    if (!resolvedStandNumber?.trim()) {
      missingFields.push(localizeByKeyStatic("FORMS.Input_Label_StandNumber"));
    }

    return missingFields;
  }, [ resolvedCompanyName, resolvedEventName, resolvedStandNumber ]);

  useEffect(() => {
    if (showModal === false) {
      return;
    }

    form.resetFields();
    form.setFieldsValue(mapTaskToFormValues(task, equipmentIdToGroupIdMap));
    setCurrentStep(getInitialStep(task));
    setMaxUnlockedStep(getInitialUnlockedStep(task, isAuditView));
    setOpenEquipmentField(null);
    setOpenContextPanels([]);
    setShowStandUpdates(
      hasStandUpdates(task.extracted_changes.stand_updates)
      || hasStandUpdates(task.final_changes.stand_updates)
    );
    setShowManagerNote(hasManagerNote(task.final_changes.manager_note));
  }, [ equipmentIdToGroupIdMap, form, isAuditView, showModal, task ]);

  useEffect(() => {
    if (isFinalizedTask) {
      if (maxUnlockedStep !== 3) {
        setMaxUnlockedStep(3);
      }

      return;
    }

    let nextUnlockedStep = maxUnlockedStep;

    if (!isMatchComplete) {
      nextUnlockedStep = 1;
    } else if (hasStepTwoBlockingIssues && nextUnlockedStep > 2) {
      nextUnlockedStep = 2;
    }

    if (nextUnlockedStep !== maxUnlockedStep) {
      setMaxUnlockedStep(nextUnlockedStep);
    }

    if (currentStep > nextUnlockedStep) {
      setCurrentStep(nextUnlockedStep);
    }
  }, [
    currentStep,
    hasStepTwoBlockingIssues,
    isFinalizedTask,
    isMatchComplete,
    maxUnlockedStep,
  ]);

  useEffect(() => {
    if (showModal === false || !equipmentIdToGroupIdMap.size) {
      return;
    }

    const currentChanges = form.getFieldValue("equipment_changes") || [];
    let hasHydratedEquipmentMetadata = false;

    const hydratedChanges = currentChanges.map((change: TCommunicationTaskFormValues["equipment_changes"][number]) => {
      if (!change?.equipment_id) {
        const nextSuggestedGroupId = change?.suggested_equipment_group_id;
        const nextEquipmentGroupId =
          change?.equipment_group_id
          || (change?.suggestion_status !== "changed" ? nextSuggestedGroupId : undefined);

        if (
          nextEquipmentGroupId === change?.equipment_group_id
          && nextSuggestedGroupId === change?.suggested_equipment_group_id
          && change?.suggestion_status
        ) {
          return change;
        }

        hasHydratedEquipmentMetadata = true;

        return {
          ...change,
          equipment_group_id: nextEquipmentGroupId,
          suggested_equipment_group_id: nextSuggestedGroupId,
          suggestion_status:
            nextSuggestedGroupId
              ? change?.suggestion_status || "pending"
              : change?.suggestion_status,
        };
      }

      const resolvedGroupId = equipmentIdToGroupIdMap.get(change.equipment_id);
      const nextSuggestedGroupId =
        change.suggested_equipment_group_id || resolvedGroupId;
      const nextEquipmentGroupId =
        change.equipment_group_id
        || (change.suggestion_status !== "changed" ? nextSuggestedGroupId : undefined);

      if (!nextEquipmentGroupId && !nextSuggestedGroupId) {
        return change;
      }

      if (
        nextEquipmentGroupId === change.equipment_group_id
        && nextSuggestedGroupId === change.suggested_equipment_group_id
        && change.suggestion_status
      ) {
        return change;
      }

      hasHydratedEquipmentMetadata = true;

      return {
        ...change,
        equipment_group_id: nextEquipmentGroupId,
        suggested_equipment_group_id: nextSuggestedGroupId,
        suggestion_status:
          nextSuggestedGroupId
            ? change.suggestion_status || "pending"
            : change.suggestion_status,
      };
    });

    if (hasHydratedEquipmentMetadata) {
      form.setFieldValue("equipment_changes", hydratedChanges);
    }
  }, [equipmentIdToGroupIdMap, form, showModal]);

  useEffect(() => {
    const nextMergedEquipmentOptions = buildMergedEquipmentOptions(
      equipmentOptions,
      task.final_changes.equipment_changes,
      equipmentIdToGroupIdMap
    );

    if (showModal === false || !nextMergedEquipmentOptions.length) {
      return;
    }

    const optionMap = new Map(
      nextMergedEquipmentOptions.map((option) => [ option.value, option.label ])
    );
    const currentChanges = form.getFieldValue("equipment_changes") || [];
    let hasPatchedSuggestions = false;

    const patchedChanges = currentChanges.map((change: TCommunicationTaskFormValues["equipment_changes"][number]) => {
      if (!change) {
        return change;
      }

      const shouldAutofillSuggestion = change.suggestion_status !== "changed";
      const nextEquipmentGroupId =
        change.equipment_group_id
        || (shouldAutofillSuggestion ? change.suggested_equipment_group_id : undefined);
      const nextItemLabel =
        change.item_label || (nextEquipmentGroupId ? optionMap.get(nextEquipmentGroupId) || "" : "");

      if (
        nextEquipmentGroupId === change.equipment_group_id
        && nextItemLabel === change.item_label
      ) {
        return change;
      }

      hasPatchedSuggestions = true;

      return {
        ...change,
        equipment_group_id: nextEquipmentGroupId,
        item_label: nextItemLabel,
      };
    });

    if (hasPatchedSuggestions) {
      form.setFieldValue("equipment_changes", patchedChanges);
    }
  }, [equipmentIdToGroupIdMap, equipmentOptions, form, showModal, task.final_changes.equipment_changes]);

  const steps = useMemo(
    () =>
      buildCommunicationTaskSteps({
        hasReviewableChanges: hasReviewableEquipmentChanges,
        isFinalized: isFinalizedTask,
        isMatchComplete,
        maxUnlockedStep,
        reviewChangesCount,
        openQuestionsCount,
      }),
    [
      hasReviewableEquipmentChanges,
      isFinalizedTask,
      isMatchComplete,
      maxUnlockedStep,
      openQuestionsCount,
      reviewChangesCount,
    ]
  );

  const mergedEquipmentOptions = useMemo(
    () =>
      buildMergedEquipmentOptions(
        equipmentOptions,
        task.final_changes.equipment_changes,
        equipmentIdToGroupIdMap
      ),
    [ equipmentIdToGroupIdMap, equipmentOptions, task.final_changes.equipment_changes ]
  );

  const equipmentOptionsMap = useMemo(
    () => new Map(mergedEquipmentOptions.map((option) => [ option.value, option.label ])),
    [mergedEquipmentOptions]
  );

  const equipmentSelectOptions = useMemo(
    () =>
      mergedEquipmentOptions.map((option) => ({
        ...option,
        searchLabel: option.label,
      })),
    [mergedEquipmentOptions]
  );

  const finalPreviewEquipmentChanges = useMemo(() => {
    const watchedChanges = watchedEquipmentChanges || [];
    const formChanges =
      (form.getFieldValue("equipment_changes") || []) as TCommunicationTaskFormValues["equipment_changes"];
    const sourceChanges =
      watchedChanges.length
        ? watchedChanges
        : formChanges.length
          ? formChanges
          : task.final_changes.equipment_changes;

    return sourceChanges
      .map((change) => {
        const resolvedEquipmentGroupId =
          change?.equipment_group_id || change?.suggested_equipment_group_id || undefined;
        const nextItemLabel =
          (resolvedEquipmentGroupId
            ? equipmentOptionsMap.get(resolvedEquipmentGroupId) || ""
            : "")
          || change?.item_label?.trim()
          || change?.equipment_title
          || "";

        return {
          ...change,
          equipment_group_id: resolvedEquipmentGroupId,
          item_label: nextItemLabel,
        };
      })
      .filter((change) => change?.item_label?.trim());
  }, [
    currentStep,
    equipmentOptionsMap,
    form,
    showModal,
    task.final_changes.equipment_changes,
    watchedEquipmentChanges,
  ]);

  const originalEmailEquipmentChanges = useMemo(
    () => [
      ...task.extracted_changes.equipment_changes,
      ...finalPreviewEquipmentChanges,
    ],
    [ finalPreviewEquipmentChanges, task.extracted_changes.equipment_changes ]
  );
  const isOriginalEmailExpanded = openContextPanels.includes("ai-context");
  const originalEmailPanelLabel = useMemo(
    () => (
      <div className="communication-original-email-collapse__trigger">
        <span className="communication-original-email-collapse__trigger-icon">
          <Icons.Document />
        </span>
        <span className="communication-original-email-collapse__trigger-copy">
          <strong>{localizeByKeyStatic("COMMUNICATION.Section_AIContextSecondary")}</strong>
        </span>
      </div>
    ),
    []
  );

  const finalPreviewSource = useMemo(
    () => ({
      event_name: resolvedEventName,
      company_name: resolvedCompanyName,
      stand_number: resolvedStandNumber,
      received_at: task.received_at,
      changes: {
        equipment_changes: finalPreviewEquipmentChanges,
        stand_updates: resolvedStandUpdates,
        manager_note: "",
      },
    }),
    [
      finalPreviewEquipmentChanges,
      resolvedCompanyName,
      resolvedEventName,
      resolvedStandNumber,
      resolvedStandUpdates,
      task.company_name,
      task.event_name,
      task.received_at,
      task.stand_number,
    ]
  );

  const extractedPreviewSource = useMemo(
    () => ({
      event_name: resolvedEventName,
      company_name: resolvedCompanyName,
      stand_number: resolvedStandNumber,
      received_at: task.received_at,
      changes: task.extracted_changes,
    }),
    [
      resolvedCompanyName,
      resolvedEventName,
      resolvedStandNumber,
      task.company_name,
      task.event_name,
      task.extracted_changes,
      task.received_at,
      task.stand_number,
    ]
  );

  const extractedLogisticsPreview = useMemo(
    () => buildCommunicationLogisticsPreview(extractedPreviewSource),
    [extractedPreviewSource]
  );

  const finalLogisticsPreview = useMemo(
    () => buildCommunicationLogisticsPreview(finalPreviewSource),
    [finalPreviewSource]
  );
  const currentStepMeta = steps.find((step) => step.id === currentStep) || steps[0];
  const isLastStep = currentStep === steps.length;
  const currentStepInsight = useMemo(() => {
    if (currentStep === 1 && matchingMissingFieldLabels.length > 0) {
      return {
        tone: "warning",
        text: localizeByKeyStatic("COMMUNICATION.StepHint_RequiredFields", {
          fields: matchingMissingFieldLabels.join(", "),
        }),
      };
    }

    if (currentStep === 2) {
      if (reviewChangesCount > 0) {
        const segments: string[] = [];

        if (pendingEquipmentSuggestionsCount > 0) {
          segments.push(
            localizeByKeyStatic("COMMUNICATION.StepHint_AIPendingCount", {
              count: pendingEquipmentSuggestionsCount,
            })
          );
        }

        if (unresolvedEquipmentSelectionCount > 0) {
          segments.push(
            localizeByKeyStatic("COMMUNICATION.StepHint_CatalogMatchCount", {
              count: unresolvedEquipmentSelectionCount,
            })
          );
        }

        return {
          tone: "warning",
          text: segments.join(" · "),
        };
      }

      if (!hasReviewableEquipmentChanges) {
        return {
          tone: "default",
          text: localizeByKeyStatic("COMMUNICATION.StepHint_NoExtractedChanges"),
        };
      }

      return {
        tone: "default",
        text: localizeByKeyStatic("COMMUNICATION.StepHint_NoPendingChanges"),
      };
    }

    if (currentStep === 3 && openQuestionsCount > 0) {
      return {
        tone: "default",
        text: localizeByKeyStatic("COMMUNICATION.Warning_OpenQuestionsCompact"),
      };
    }

    return null;
  }, [
    currentStep,
    hasReviewableEquipmentChanges,
    matchingMissingFieldLabels,
    openQuestionsCount,
    pendingEquipmentSuggestionsCount,
    reviewChangesCount,
    unresolvedEquipmentSelectionCount,
  ]);

  const getStepBadgeLabel = (step: TCommunicationTaskStep): string =>
    localizeByKeyStatic(
      step.badgeTextKey,
      step.count > 0 ? { count: step.count } : undefined
    );

  const getStepTitle = (step: TCommunicationTaskStep): string =>
    stripStepTitleIndex(localizeByKeyStatic(step.titleKey));

  const renderStepIcon = (step: TCommunicationTaskStep) => {
    const StepIcon = stepIconMap[step.icon];

    return <StepIcon />;
  };

  const scrollToFirstFieldError = (error: TValidateFieldsError) => {
    const firstField = error?.errorFields?.[0];

    if (firstField?.name) {
      form.scrollToField(firstField.name, { block: "center" });
    }
  };

  const waitForStepRender = () =>
    new Promise<void>((resolve) => {
      setTimeout(() => resolve(), 0);
    });

  const getStepTwoValidationFieldNames = (
    equipmentChanges: TCommunicationTaskFormValues["equipment_changes"]
  ): TValidationFieldName[] =>
    equipmentChanges.flatMap((change, index, changes) => {
      if (shouldIgnoreEquipmentChangeValidation(change, index, changes, hasExistingEquipmentChanges)) {
        return [];
      }

      return [
        [ "equipment_changes", index, "equipment_group_id" ],
        [ "equipment_changes", index, "qty" ],
      ];
    });

  const validateStepFields = async (
    stepId: number,
    fieldNames: TValidationFieldName[]
  ): Promise<boolean> => {
    if (!fieldNames.length) {
      return true;
    }

    if (currentStep !== stepId) {
      setCurrentStep(stepId);
      await waitForStepRender();
    }

    try {
      await form.validateFields(fieldNames);
      return true;
    } catch (error) {
      scrollToFirstFieldError(error as TValidateFieldsError);
      return false;
    }
  };

  const handleNextStep = async () => {
    if (currentStep === 1) {
      const isStepOneValid = await validateStepFields(1, [ ...matchingFieldNames ]);

      if (!isStepOneValid) {
        return;
      }

      setMaxUnlockedStep((step) => (step < 2 ? 2 : step));
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2) {
      if (!isMatchComplete) {
        await validateStepFields(1, [ ...matchingFieldNames ]);
        return;
      }

      const isStepTwoValid = await validateStepFields(
        2,
        getStepTwoValidationFieldNames(resolvedEquipmentChanges)
      );

      if (!isStepTwoValid) {
        return;
      }

      setMaxUnlockedStep((step) => (step < 3 ? 3 : step));
      setCurrentStep(3);
      return;
    }
  };

  const handleApproveClick = async () => {
    if (!isMatchComplete) {
      await validateStepFields(1, [ ...matchingFieldNames ]);
      return;
    }

    if (hasStepTwoBlockingIssues) {
      await validateStepFields(2, getStepTwoValidationFieldNames(resolvedEquipmentChanges));
      return;
    }

    void form.validateFields().then((validatedValues) => {
      const rawValues = form.getFieldsValue(true) as Partial<TCommunicationTaskFormValues>;
      const nextValues = buildFormValueSnapshot(task, {
        ...rawValues,
        ...validatedValues,
      }, {
        event_name: resolvedEventName,
        company_name: resolvedCompanyName,
        stand_number: resolvedStandNumber,
        equipment_changes: resolvedEquipmentChanges,
        stand_updates: resolvedStandUpdates,
        questions: resolvedQuestions,
      });

      onApprove(task.id, nextValues);
      action?.();
    }).catch((error: TValidateFieldsError) => {
      scrollToFirstFieldError(error);
    });
  };

  const patchEquipmentChange = (
    index: number,
    patch: Partial<TCommunicationTaskFormValues["equipment_changes"][number]>
  ) => {
    const equipmentChanges = form.getFieldValue("equipment_changes") || [];
    const nextEquipmentChanges = equipmentChanges.map(
      (
        change: TCommunicationTaskFormValues["equipment_changes"][number],
        changeIndex: number
      ) => (changeIndex === index ? { ...change, ...patch } : change)
    );

    form.setFieldValue("equipment_changes", nextEquipmentChanges);
  };

  const handleQuestionStatusToggle = (index: number, checked: boolean) => {
    form.setFieldValue(
      [ "questions", index, "status" ],
      checked ? "reviewed" : "open"
    );
  };

  const syncEquipmentChangeLabel = (index: number, equipmentGroupId?: string) => {
    const nextLabel = equipmentGroupId ? equipmentOptionsMap.get(equipmentGroupId) || "" : "";
    patchEquipmentChange(index, { item_label: nextLabel });
  };

  const handleConfirmEquipmentSuggestion = (index: number) => {
    const currentChange = form.getFieldValue([ "equipment_changes", index ]) || {};
    const nextEquipmentGroupId =
      currentChange.equipment_group_id || currentChange.suggested_equipment_group_id;

    patchEquipmentChange(index, {
      equipment_group_id: nextEquipmentGroupId,
      suggestion_status: "confirmed",
    });
    syncEquipmentChangeLabel(index, nextEquipmentGroupId);
  };

  const handleChooseAnotherEquipment = (index: number) => {
    patchEquipmentChange(index, {
      equipment_group_id: undefined,
      item_label: "",
      suggestion_status: "changed",
    });
    setOpenEquipmentField(index);
  };

  const handleEquipmentSelectChange = (index: number, equipmentGroupId?: string) => {
    const currentChange = form.getFieldValue([ "equipment_changes", index ]) || {};
    const suggestionMeta = getEquipmentSuggestionMeta(currentChange);

    patchEquipmentChange(index, {
      equipment_group_id: equipmentGroupId,
      suggestion_status:
        suggestionMeta
          ? equipmentGroupId && equipmentGroupId === suggestionMeta.suggestedGroupId
            ? "pending"
            : "changed"
          : currentChange.suggestion_status,
    });
    syncEquipmentChangeLabel(index, equipmentGroupId);

    if (equipmentGroupId) {
      setOpenEquipmentField(null);
    }
  };

  const renderLogisticsTable = (
    rows: ReturnType<typeof buildCommunicationLogisticsPreview>,
    emptyKey: string,
    productLabelKey = "COMMUNICATION.Preview_Product"
  ) => {
    if (!rows.length) {
      return (
        <div className="communication-task-form__preview-empty">
          <Localize>{emptyKey}</Localize>
        </div>
      );
    }

    return (
      <div className="communication-task-form__logistics-table">
        <div className="communication-task-form__logistics-head">
          <strong className="communication-task-form__logistics-head-cell">
            <span
              className="communication-task-form__logistics-head-copy"
              title={localizeByKeyStatic("COMMUNICATION.Filter_Event")}
            >
              <Localize>COMMUNICATION.Filter_Event</Localize>
            </span>
          </strong>
          <strong className="communication-task-form__logistics-head-cell communication-task-form__logistics-head-cell--action">
            <span
              className="communication-task-form__logistics-head-copy"
              title={localizeByKeyStatic("FORMS.Input_Label_Action")}
            >
              <Localize>FORMS.Input_Label_Action</Localize>
            </span>
          </strong>
          <strong className="communication-task-form__logistics-head-cell">
            <span
              className="communication-task-form__logistics-head-copy"
              title={localizeByKeyStatic(productLabelKey)}
            >
              <Localize>{productLabelKey}</Localize>
            </span>
          </strong>
          <strong className="communication-task-form__logistics-head-cell">
            <span
              className="communication-task-form__logistics-head-copy"
              title={localizeByKeyStatic("FORMS.Input_Label_CompanyName")}
            >
              <Localize>FORMS.Input_Label_CompanyName</Localize>
            </span>
          </strong>
          <strong className="communication-task-form__logistics-head-cell">
            <span
              className="communication-task-form__logistics-head-copy"
              title={localizeByKeyStatic("FORMS.Input_Label_StandNumber")}
            >
              <Localize>FORMS.Input_Label_StandNumber</Localize>
            </span>
          </strong>
          <strong className="communication-task-form__logistics-head-cell communication-task-form__logistics-head-cell--qty">
            <span
              className="communication-task-form__logistics-head-copy"
              title={localizeByKeyStatic("COMMUNICATION.Preview_TotalQty")}
            >
              <Localize>COMMUNICATION.Preview_TotalQty</Localize>
            </span>
          </strong>
          <strong className="communication-task-form__logistics-head-cell communication-task-form__logistics-head-cell--date">
            <span
              className="communication-task-form__logistics-head-copy"
              title={localizeByKeyStatic("COMMUNICATION.Preview_EmailDate")}
            >
              <Localize>COMMUNICATION.Preview_EmailDate</Localize>
            </span>
          </strong>
        </div>

        {rows.map((row) => (
            <div key={row.id} className="communication-task-form__logistics-row">
            <span className="communication-task-form__logistics-cell">
              <span
                className="communication-task-form__logistics-cell-copy"
                title={row.event_name || "\u2014"}
              >
                {row.event_name || "\u2014"}
              </span>
            </span>
            <span className="communication-task-form__logistics-cell communication-task-form__logistics-cell--action">
              <span
                className={`communication-task-form__action-tag communication-task-form__action-tag--${row.action}`}
              >
                <Localize>{actionLabelKeyMap[row.action]}</Localize>
              </span>
            </span>
            <span className="communication-task-form__logistics-cell">
              <span
                className="communication-task-form__logistics-cell-copy"
                title={row.product || "\u2014"}
              >
                {row.product || "\u2014"}
              </span>
            </span>
            <span className="communication-task-form__logistics-cell">
              <span
                className="communication-task-form__logistics-cell-copy"
                title={row.company_name || "\u2014"}
              >
                {row.company_name || "\u2014"}
              </span>
            </span>
            <span className="communication-task-form__logistics-cell">
              <span
                className="communication-task-form__logistics-cell-copy"
                title={row.stand_number || "\u2014"}
              >
                {row.stand_number || "\u2014"}
              </span>
            </span>
            <span className="communication-task-form__logistics-cell communication-task-form__logistics-cell--qty">
              {row.quantity}
            </span>
            <span className="communication-task-form__logistics-cell communication-task-form__logistics-cell--date">
              {row.received_at}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Form
      form={form}
      layout="vertical"
      className="model-form communication-task-form"
      initialValues={mapTaskToFormValues(task, equipmentIdToGroupIdMap)}
    >
      <div className="communication-task-form__meta">
        <div className="communication-task-form__meta-item">
          <span><Localize>FORMS.Input_Label_Sender</Localize></span>
          <strong className="communication-task-form__meta-value" title={task.sender}>
            {task.sender}
          </strong>
        </div>
        <div className="communication-task-form__meta-item">
          <span><Localize>FORMS.Input_Label_ReceivedAt</Localize></span>
          <strong className="communication-task-form__meta-value">
            {formatCommunicationDateTime(task.received_at)}
          </strong>
        </div>
        <div className="communication-task-form__meta-item">
          <span><Localize>FORMS.Input_Label_Subject</Localize></span>
          <strong className="communication-task-form__meta-value" title={task.subject}>
            {task.subject}
          </strong>
        </div>
        <div
          className={`communication-task-form__meta-item communication-task-form__meta-item--confidence communication-task-form__meta-item--confidence-${confidenceTone}`}
        >
          <span><Localize>FORMS.Input_Label_Confidence</Localize></span>
          <div className="communication-task-form__confidence-value">
            <strong className="communication-task-form__confidence-percent">{`${confidenceValue}%`}</strong>
            <span className={`communication-task-form__confidence-chip communication-task-form__confidence-chip--${confidenceTone}`}>
              <Localize>
                {confidenceTone === "high"
                  ? "COMMUNICATION.Confidence_High"
                  : confidenceTone === "medium"
                    ? "COMMUNICATION.Confidence_Medium"
                    : "COMMUNICATION.Confidence_Low"}
              </Localize>
            </span>
          </div>
          <span className="communication-task-form__confidence-track" aria-hidden={true}>
            <span
              className={`communication-task-form__confidence-fill communication-task-form__confidence-fill--${confidenceTone}`}
              style={{ width: `${confidenceValue}%` }}
            />
          </span>
        </div>
      </div>

      <div className="communication-task-form__steps">
        {steps.map((step) => (
          <button
            key={step.id}
            type="button"
            className={`communication-task-form__step communication-task-form__step--${step.status} ${currentStep === step.id ? "is-current" : ""}`}
            onClick={() => setCurrentStep(step.id)}
            disabled={!step.isClickable}
            aria-current={currentStep === step.id ? "step" : undefined}
            aria-label={`${getStepTitle(step)}. ${getStepBadgeLabel(step)}`}
          >
            <span className="communication-task-form__step-number">{step.id}</span>
            <span
              className={`communication-task-form__step-icon communication-task-form__step-icon--${step.status}`}
              aria-hidden={true}
            >
              {renderStepIcon(step)}
            </span>
            <span className="communication-task-form__step-copy">
              <span className="communication-task-form__step-text">{getStepTitle(step)}</span>
              <span className={`communication-task-form__step-badge communication-task-form__step-badge--${step.status}`}>
                {getStepBadgeLabel(step)}
              </span>
            </span>
          </button>
        ))}
      </div>

      <div className={`communication-task-form__step-help communication-task-form__step-help--${currentStepMeta?.status || "optional"}`}>
        <strong>{currentStepMeta ? getStepTitle(currentStepMeta) : ""}</strong>
        <span>{currentStepMeta ? localizeByKeyStatic(currentStepMeta.descriptionKey) : ""}</span>
        {currentStepInsight?.text && (
          <span
            className={`communication-task-form__step-help-status communication-task-form__step-help-status--${currentStepInsight.tone}`}
          >
            {currentStepInsight.text}
          </span>
        )}
      </div>

      {currentStep === 3 && (
        <div className="communication-task-form__preview-card communication-task-form__preview-card--full">
          <div className="communication-task-form__preview-header">
            <span className="communication-task-form__preview-title">
              <Localize>COMMUNICATION.Preview_LogisticsFinal</Localize>
            </span>
            <span className="communication-task-form__preview-count">
              <Localize count={finalLogisticsPreview.length}>COMMUNICATION.Text_ChangesCount</Localize>
            </span>
          </div>
          <small className="communication-task-form__preview-hint">
            <Localize>COMMUNICATION.Preview_LogisticsFinalHint</Localize>
          </small>

          {renderLogisticsTable(
            finalLogisticsPreview,
            "COMMUNICATION.Text_NoFinalChanges"
          )}
        </div>
      )}

      {currentStep === 1 && (
        <div className="communication-task-form__section">
          <div className="row-grid grid-gap-10 row-gap-0 col-md-2">
            <Form.Item
              name="event_name"
              label={<Localize>FORMS.Input_Label_EventName</Localize>}
              rules={[{
                required: true,
                whitespace: true,
                message: localizeByKeyStatic("FORM_RULES.Required_EventName"),
              }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="company_name"
              label={<Localize>FORMS.Input_Label_CompanyName</Localize>}
              rules={[{
                required: true,
                whitespace: true,
                message: localizeByKeyStatic("FORM_RULES.Required_CompanyName"),
              }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="stand_number"
              label={<Localize>FORMS.Input_Label_StandNumber</Localize>}
              rules={[{
                required: true,
                whitespace: true,
                message: localizeByKeyStatic("FORM_RULES.Required_StandNumber"),
              }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="organizer_id" label={<Localize>FORMS.Input_Label_Organizer</Localize>}>
              <Select
                allowClear
                options={organizerOptions}
                suffixIcon={<Icons.Arrow />}
                placeholder={localizeByKeyStatic("FORMS.Input_Placeholder_Choose")}
              />
            </Form.Item>
            <Form.Item
              name="exhibition_type_id"
              label={<Localize>FORMS.Input_Label_ExhibitionTypes</Localize>}
            >
              <Select
                allowClear
                options={exhibitionOptions}
                suffixIcon={<Icons.Arrow />}
                placeholder={localizeByKeyStatic("FORMS.Input_Placeholder_Choose")}
              />
            </Form.Item>
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <div className="communication-task-form__section">
          <div className="communication-task-form__preview-card communication-task-form__preview-card--full">
            <div className="communication-task-form__preview-header">
              <span className="communication-task-form__preview-title">
                <Localize>COMMUNICATION.Preview_LogisticsExtracted</Localize>
              </span>
              <span className="communication-task-form__preview-count">
                <Localize count={extractedLogisticsPreview.length}>COMMUNICATION.Text_ChangesCount</Localize>
              </span>
            </div>
            <small className="communication-task-form__preview-hint">
              <Localize>COMMUNICATION.Preview_LogisticsExtractedHint</Localize>
            </small>

            {renderLogisticsTable(
              extractedLogisticsPreview,
              "COMMUNICATION.Text_NoExtractedChanges",
              "COMMUNICATION.Preview_ItemFromEmail"
            )}
          </div>

          {pendingEquipmentSuggestionsCount > 0 && (
            <Alert
              className="communication-task-form__suggestions-alert"
              showIcon
              type="warning"
              message={
                <Localize count={pendingEquipmentSuggestionsCount}>
                  COMMUNICATION.EquipmentSuggestion_PendingSummary
                </Localize>
              }
            />
          )}

          <Form.List name="equipment_changes">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => {
                  const currentChange = watchedEquipmentChanges?.[field.name] || {};
                  const equipmentLabel =
                    equipmentOptionsMap.get(getEffectiveEquipmentGroupId(currentChange) || "") || "";
                  const suggestionMeta = getEquipmentSuggestionMeta(currentChange);

                  return (
                    <div
                      key={field.key}
                      className={`row-grid grid-gap-10 row-gap-0 communication-task-form__row communication-task-form__change-grid${suggestionMeta ? ` communication-task-form__change-grid--${suggestionMeta.status}` : ""}`}
                    >
                    <Form.Item
                      name={[field.name, "item_label"]}
                      hidden={true}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "equipment_id"]}
                      hidden={true}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "equipment_title"]}
                      hidden={true}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "suggested_equipment_group_id"]}
                      hidden={true}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "suggestion_status"]}
                      hidden={true}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "action"]}
                      label={<Localize>FORMS.Input_Label_Action</Localize>}
                    >
                      <Select suffixIcon={<Icons.Arrow />} options={actionOptions} />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "equipment_group_id"]}
                      label={
                        <div className="communication-task-form__equipment-label">
                          <span className="communication-task-form__equipment-label-text">
                            <Localize>FORMS.Input_Label_Equipment</Localize>
                          </span>

                          {suggestionMeta && (
                            <div
                              className={`communication-task-form__equipment-suggestion communication-task-form__equipment-suggestion--${suggestionMeta.status}`}
                            >
                              <span className="communication-task-form__equipment-suggestion-badge">
                                <Localize>
                                  {suggestionMeta.status === "pending"
                                    ? "COMMUNICATION.EquipmentSuggestion_Badge"
                                    : suggestionMeta.status === "confirmed"
                                      ? "COMMUNICATION.EquipmentSuggestion_ConfirmedBadge"
                                      : "COMMUNICATION.EquipmentSuggestion_ChangedBadge"}
                                </Localize>
                              </span>

                              {suggestionMeta.status === "pending" ? (
                                <div className="communication-task-form__equipment-suggestion-actions">
                                  <Button
                                    type="link"
                                    icon={<Icons.Check />}
                                    onClick={() => handleConfirmEquipmentSuggestion(field.name)}
                                  >
                                    <Localize>GLOBAL.Button_Text_Confirm</Localize>
                                  </Button>
                                  <Button
                                    type="link"
                                    onClick={() => handleChooseAnotherEquipment(field.name)}
                                  >
                                    <Localize>COMMUNICATION.Button_ChooseAnother</Localize>
                                  </Button>
                                </div>
                              ) : (
                                <span className="communication-task-form__equipment-suggestion-status">
                                  <Localize>{suggestionStatusLabelKeyMap[suggestionMeta.status]}</Localize>
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      }
                      rules={[{
                        validator: (_, value) => {
                          const equipmentChanges =
                            (form.getFieldValue("equipment_changes") || []) as TCommunicationTaskFormValues["equipment_changes"];
                          const currentEquipmentChange = equipmentChanges[field.name];

                          if (
                            shouldIgnoreEquipmentChangeValidation(
                              currentEquipmentChange,
                              field.name,
                              equipmentChanges,
                              hasExistingEquipmentChanges
                            )
                          ) {
                            return Promise.resolve();
                          }

                          if ((typeof value === "string" && value.trim()) || getEffectiveEquipmentGroupId(currentEquipmentChange)) {
                            return Promise.resolve();
                          }

                          return Promise.reject(
                            new Error(getEquipmentValidationMessage(currentEquipmentChange))
                          );
                        },
                      }]}
                    >
                      <Select
                        key={`${field.key}-${currentChange?.equipment_group_id || currentChange?.suggested_equipment_group_id || "empty"}-${mergedEquipmentOptions.length}`}
                        suffixIcon={<Icons.Arrow />}
                        allowClear
                        showSearch
                        open={openEquipmentField === field.name ? true : undefined}
                        options={equipmentSelectOptions}
                        optionLabelProp="label"
                        optionFilterProp="searchLabel"
                        popupClassName="communication-task-form__equipment-dropdown"
                        placeholder={localizeByKeyStatic("FORMS.Input_Placeholder_Choose")}
                        title={equipmentLabel.length > 40 ? equipmentLabel : undefined}
                        onChange={(value) => handleEquipmentSelectChange(field.name, value)}
                        onDropdownVisibleChange={(isOpen) =>
                          setOpenEquipmentField(isOpen ? field.name : null)
                        }
                      />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "qty"]}
                      label={<Localize>FORMS.Input_Label_Amount</Localize>}
                      rules={[{ required: true, message: localizeByKeyStatic("FORM_RULES.Required_Amount") }]}
                    >
                      <InputNumber min={1} style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "color"]}
                      label={<Localize>FORMS.Input_Label_Color</Localize>}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item label=" ">
                      <Button
                        icon={<Icons.Delete />}
                        onClick={() => {
                          setOpenEquipmentField(null);
                          remove(field.name);
                        }}
                      />
                    </Form.Item>
                    </div>
                  );
                })}

                <div className="communication-task-form__add-actions">
                  <div className="communication-task-form__add-actions-primary">
                    <Button
                      className="light-bg"
                      icon={<Icons.Plus />}
                      onClick={() => add(createEmptyEquipmentChange(task.id))}
                    >
                      <span className="ellipsis">
                        <Localize>COMMUNICATION.Button_AddChange</Localize>
                      </span>
                    </Button>
                  </div>

                  {/*
                  (!showStandUpdates || !showManagerNote) && (
                    <div className="communication-task-form__add-actions-secondary">
                      {!showStandUpdates && (
                        <Button
                          className="light-bg"
                          icon={<Icons.Plus />}
                          onClick={() => setShowStandUpdates(true)}
                        >
                          <span className="ellipsis">
                            <Localize>COMMUNICATION.Button_AddStandUpdate</Localize>
                          </span>
                        </Button>
                      )}

                      {!showManagerNote && (
                        <Button
                          className="light-bg"
                          icon={<Icons.Plus />}
                          onClick={() => setShowManagerNote(true)}
                        >
                          <span className="ellipsis">
                            <Localize>COMMUNICATION.Button_AddManagerNote</Localize>
                          </span>
                        </Button>
                      )}
                    </div>
                  )}
                  */}
                </div>
              </>
            )}
          </Form.List>

          {showStandUpdates && (
            <div className="row-grid grid-gap-10 row-gap-0 col-md-2 communication-task-form__flooring">
              <Form.Item
                name={["stand_updates", "flooring_id"]}
                label={<Localize>FORMS.Input_Label_Flooring</Localize>}
              >
                <Select
                  suffixIcon={<Icons.Arrow />}
                  allowClear
                  options={flooringOptions}
                  placeholder={localizeByKeyStatic("FORMS.Input_Placeholder_Choose")}
                />
              </Form.Item>
              <Form.Item
                name={["stand_updates", "flooring_color"]}
                label={<Localize>FORMS.Input_Label_CarpetColor</Localize>}
              >
                <Input />
              </Form.Item>
            </div>
          )}

          {showManagerNote && (
            <Form.Item name="manager_note" label={<Localize>FORMS.Input_Label_ManagerNote</Localize>}>
              <TextArea rows={4} />
            </Form.Item>
          )}
        </div>
      )}

      {currentStep === 3 && (
        <div className="communication-task-form__section">
          <Form.List name="questions">
            {(fields, { add, remove }) => (
              <div className="communication-task-form__questions-panel">
                <div className="communication-task-form__questions-header">
                  <div className="communication-task-form__questions-copy">
                    <span className="communication-task-form__questions-eyebrow">
                      <Localize>COMMUNICATION.Preview_QuestionsOverview</Localize>
                    </span>
                    <strong className="communication-task-form__questions-title">
                      <Localize
                        questions={resolvedQuestions.length}
                        open={openQuestionsCount}
                      >
                        COMMUNICATION.Text_QuestionsSummary
                      </Localize>
                    </strong>
                    <span className="communication-task-form__questions-help">
                      <Localize>COMMUNICATION.Step_Questions_Help</Localize>
                    </span>
                  </div>

                  <div className="communication-task-form__questions-actions">
                    <span
                      className={`communication-task-form__questions-badge${openQuestionsCount > 0 ? " communication-task-form__questions-badge--warning" : ""}`}
                    >
                      {fields.length > 0 ? (
                        <Localize
                          questions={resolvedQuestions.length}
                          open={openQuestionsCount}
                        >
                          COMMUNICATION.Text_QuestionsSummary
                        </Localize>
                      ) : (
                        <Localize>COMMUNICATION.Text_NoQuestions</Localize>
                      )}
                    </span>

                    <Button
                      className="light-bg"
                      icon={<Icons.Plus />}
                      onClick={() => add(createEmptyQuestion(task.id))}
                    >
                      <span className="ellipsis">
                        <Localize>COMMUNICATION.Button_AddQuestion</Localize>
                      </span>
                    </Button>
                  </div>
                </div>

                <div className="communication-task-form__question-list">
                  {fields.length === 0 && (
                    <div className="communication-task-form__questions-empty">
                      <Localize>COMMUNICATION.Text_NoQuestions</Localize>
                    </div>
                  )}

                  {fields.map((field, index) => {
                    const currentQuestion = resolvedQuestions?.[field.name];
                    const questionStatus = currentQuestion?.status || "open";

                    return (
                      <div
                        key={field.key}
                        className={`communication-task-form__question-card communication-task-form__question-card--${questionStatus}`}
                      >
                        <div className="communication-task-form__question-card-header">
                          <div className="communication-task-form__question-card-copy">
                            <strong className="communication-task-form__question-card-title">
                              {`${localizeByKeyStatic("COMMUNICATION.Field_Question")} #${index + 1}`}
                            </strong>
                            <span
                              className={`communication-task-form__question-card-state communication-task-form__question-card-state--${questionStatus}`}
                            >
                              {questionStatusLabelMap[questionStatus]}
                            </span>
                          </div>

                          <div className="communication-task-form__question-card-actions">
                            <Checkbox
                              className="communication-task-form__question-toggle"
                              checked={questionStatus === "reviewed"}
                              onChange={(event) =>
                                handleQuestionStatusToggle(field.name, event.target.checked)
                              }
                            >
                              <Localize>COMMUNICATION.QuestionStatus_Reviewed</Localize>
                            </Checkbox>
                          </div>
                        </div>

                        <div className="communication-task-form__question-row">
                          <Form.Item
                            className="communication-task-form__question-main"
                            name={[field.name, "text"]}
                            label={<Localize>COMMUNICATION.Field_Question</Localize>}
                            rules={[
                              {
                                required: true,
                                message: localizeByKeyStatic("COMMUNICATION.Validation_Question"),
                              },
                            ]}
                          >
                            <TextArea rows={4} />
                          </Form.Item>

                          <div className="communication-task-form__question-side">
                            <Form.Item
                              name={[field.name, "status"]}
                              label={<Localize>COMMUNICATION.Field_QuestionStatus</Localize>}
                            >
                              <Select suffixIcon={<Icons.Arrow />} options={questionStatusOptions} />
                            </Form.Item>

                            <Button
                              className="light-bg communication-task-form__question-delete"
                              icon={<Icons.Delete />}
                              onClick={() => remove(field.name)}
                            >
                              <span className="ellipsis">
                                <Localize>GLOBAL.Button_Text_Remove</Localize>
                              </span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Form.List>

          {openQuestionsCount > 0 && (
            <div className="communication-task-form__questions-note">
              <span className="communication-task-form__questions-note-icon">
                <Icons.Information />
              </span>
              <span className="communication-task-form__questions-note-text">
                <Localize>COMMUNICATION.Warning_OpenQuestionsCompact</Localize>
              </span>
            </div>
          )}
        </div>
      )}

      <div className="communication-task-form__section">
        <Collapse
          className="communication-original-email-collapse"
          expandIconPosition="end"
          activeKey={openContextPanels}
          onChange={(keys) => setOpenContextPanels(normalizeCollapseKeys(keys as string | string[]))}
          items={[
            {
              key: "ai-context",
              label: originalEmailPanelLabel,
              children: (
                <div className="row-grid grid-gap-10 row-gap-0 communication-original-email-collapse__content">
                  <div className="communication-original-email-collapse__viewer">
                    <CommunicationOriginalEmailField
                      originalBody={task.original_body}
                      equipmentChanges={originalEmailEquipmentChanges}
                      isExpanded={isOriginalEmailExpanded}
                    />
                  </div>
                </div>
              ),
            },
          ]}
        />
      </div>

      {auditEntry && (
        <div className="communication-task-form__section">
          <h4><Localize>COMMUNICATION.Section_Audit</Localize></h4>
          <div className="communication-audit-box">
            <div>
              <strong><Localize>TABLES.Column_Title_ApprovedBy</Localize>:</strong> {auditEntry.approved_by}
            </div>
            <div>
              <strong><Localize>TABLES.Column_Title_ApprovedAt</Localize>:</strong>{" "}
              {formatCommunicationDateTime(auditEntry.approved_at)}
            </div>
          </div>
        </div>
      )}

      <div className="form-btn-holder communication-task-form__actions">
        <div className="communication-task-form__actions-start">
          <Button className="light-bg" onClick={() => action?.()}>
            <span className="ellipsis">
              <Localize>GLOBAL.Button_Text_Cancel</Localize>
            </span>
          </Button>

          {currentStep > 1 && (
            <Button
              className="light-bg"
              onClick={() => setCurrentStep((step) => Math.max(1, step - 1))}
            >
              <span className="ellipsis">
                <Localize>COMMUNICATION.Button_Back</Localize>
              </span>
            </Button>
          )}
        </div>

        <div className="communication-task-form__actions-end">
          {!isLastStep && (
            <Button
              type="primary"
              onClick={handleNextStep}
            >
              <span className="ellipsis">
                <Localize>COMMUNICATION.Button_Next</Localize>
              </span>
            </Button>
          )}

          {isLastStep && canApprove && !isAuditView && (
            <Button
              type="primary"
              className="btn-right"
              icon={<Icons.Check />}
              onClick={handleApproveClick}
            >
              <span className="ellipsis">
                <Localize>GLOBAL.Button_Text_Approve</Localize>
              </span>
            </Button>
          )}
        </div>
      </div>
    </Form>
  );
};

export default CommunicationTaskForm;
